import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import { useProfile } from 'context/profile';
import { PERMISSIONS } from 'models/Profile';
import { ReviewStatus, ReviewType } from 'models/Review';
import { coordinateRequestQueryKeys, indexReviews, ReviewsIndexParams } from 'services/api/reviews';
import colors from 'styles/theme/colors';
import { LabelBold } from 'styles/typography';
import CaretRight from 'svg/CaretRight';
import CoordinateRequestIcon from 'svg/CoordinateRequestIcon';

export default function CoordinationRequestsBanner() {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const canSeeRequests = profile.has(PERMISSIONS.reviewCoordinateRequestEdit);

  const { data } = useQuery({
    queryKey: coordinateRequestQueryKeys.count(),
    queryFn: ({ signal }) =>
      indexReviews(
        {
          type: ReviewType.COORDINATE_REQUEST,
          filters: { status: ReviewStatus.PENDING } as unknown as ReviewsIndexParams['filters'],
          pageSize: 1,
        },
        signal
      ),
    enabled: canSeeRequests,
  });

  const count = data?.meta?.totalRecords ?? 0;

  if (!canSeeRequests || count === 0) return null;

  return (
    <Container
      data-testid='coordination-requests-banner'
      onClick={() => navigate('/coordinate/requests')}
      role='button'
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          navigate('/coordinate/requests');
        }
      }}>
      <IconWrapper>
        <CoordinateRequestIcon />
      </IconWrapper>
      <Copy>
        <LabelBold>Requests ({count})</LabelBold>
        <Subtitle>You have new patients to review and add into Olio.</Subtitle>
      </Copy>
      <ViewLink>
        View Requests <CaretRight height={12} />
      </ViewLink>
    </Container>
  );
}

const Container = styled.div`
  align-items: center;
  background-color: ${colors.primaryBlue10};
  border: 1px solid ${colors.primaryBlue25};
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  gap: 16px;
  margin: 24px 24px 0;
  padding: 12px 24px;

  &:hover {
    background-color: ${colors.primaryBlue15};
  }
`;

const IconWrapper = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  justify-content: center;
`;

const Copy = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 2px;
`;

const Subtitle = styled.span`
  color: ${colors.black75};
  font-size: 12px;
  line-height: 18px;
`;

const ViewLink = styled.span`
  align-items: center;
  color: ${colors.primaryBlue};
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 14px;
  font-weight: 600;
  gap: 4px;
`;
